import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue, useSetRecoilState } from 'recoil'
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Divider, Grid, IconButton,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography, makeStyles
} from '@material-ui/core'
import AssessmentIcon from '@material-ui/icons/Assessment'
import PeopleIcon from '@material-ui/icons/People'
import SwapHorizIcon from '@material-ui/icons/SwapHoriz'
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount'
import BusinessIcon from '@material-ui/icons/Business'
import PersonIcon from '@material-ui/icons/Person'
import SearchIcon from '@material-ui/icons/Search'
import CloseIcon from '@material-ui/icons/Close'
import RefreshIcon from '@material-ui/icons/Refresh'
import Layout from '../../components/layout/Layout'
import RowActions from '../../components/common/RowActions'
import api from '../../api/client'
import { gregorianToJalali, thousandsSeparate, toFarsiDigits, toEnglishDigits } from '../../assets/utils'
import { logoSrc } from '../../api/logoSrc'
import { beginImpersonation } from '../../api/impersonation'
import { userInfoState } from '../../recoil/atoms'

const useStyles = makeStyles((theme) => ({
  root: { background: '#f1f3f8', minHeight: '100%', paddingBottom: 24 },

  hero: {
    position: 'relative', overflow: 'hidden', borderRadius: 20,
    padding: '20px 24px', marginBottom: 16,
    background: 'linear-gradient(135deg, #1a237e 0%, #283593 55%, #3f51b5 100%)',
    color: '#fff', boxShadow: '0 10px 26px rgba(26,35,126,.28)',
    display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap',
  },
  heroBlob: {
    position: 'absolute', width: 190, height: 190, borderRadius: '50%',
    background: 'rgba(255,255,255,.08)', top: -80, left: -50,
  },
  heroBlob2: {
    position: 'absolute', width: 120, height: 120, borderRadius: '50%',
    background: 'rgba(255,255,255,.07)', bottom: -60, right: 90,
  },
  logo: {
    position: 'relative', width: 72, height: 72, borderRadius: 18,
    background: 'rgba(255,255,255,.16)', border: '1.5px solid rgba(255,255,255,.45)',
    objectFit: 'cover',
  },
  logoBox: {
    position: 'relative', width: 72, height: 72, borderRadius: 18,
    background: 'rgba(255,255,255,.16)', border: '1.5px solid rgba(255,255,255,.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  heroTitle: { position: 'relative', fontSize: 21, fontWeight: 900 },
  heroSub: { position: 'relative', fontSize: 13, opacity: .9, marginTop: 2 },
  heroChips: { position: 'relative', display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 },
  heroChip: { background: 'rgba(255,255,255,.18)', color: '#fff', fontWeight: 700, fontSize: 11.5 },

  // ── مشخصاتِ خودِ مدیر سازمان ──
  heroManager: {
    position: 'relative', display: 'flex', alignItems: 'center', gap: 12,
    padding: '9px 16px', borderRadius: 14,
    background: 'rgba(255,255,255,.14)', border: '1px solid rgba(255,255,255,.3)',
  },
  managerAvatar: {
    width: 52, height: 52, borderRadius: 14, objectFit: 'cover',
    border: '2px solid rgba(255,255,255,.7)', background: 'rgba(255,255,255,.2)',
  },
  managerAvatarBox: {
    width: 52, height: 52, borderRadius: 14, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,.22)', border: '1.5px solid rgba(255,255,255,.45)',
  },
  heroManagerLabel: { position: 'relative', fontSize: 11, opacity: .85, fontWeight: 700 },
  heroManagerName: { position: 'relative', fontSize: 15, fontWeight: 900, lineHeight: 1.5 },
  heroManagerCode: { position: 'relative', fontSize: 11.5, opacity: .85 },

  statCard: {
    position: 'relative', overflow: 'hidden', borderRadius: 16, padding: '15px 18px',
    display: 'flex', alignItems: 'center', gap: 14, color: '#fff',
    boxShadow: '0 6px 18px rgba(20,30,80,.16)',
    transition: 'transform .18s ease, box-shadow .18s ease',
    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 12px 28px rgba(20,30,80,.26)' },
  },
  blob: {
    position: 'absolute', width: 120, height: 120, borderRadius: '50%',
    background: 'rgba(255,255,255,.16)', top: -46, left: -26,
  },
  blob2: {
    position: 'absolute', width: 70, height: 70, borderRadius: '50%',
    background: 'rgba(255,255,255,.12)', bottom: -30, right: 18,
  },
  iconBox: {
    position: 'relative', width: 44, height: 44, borderRadius: 14,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(255,255,255,.22)', flexShrink: 0,
  },
  value: { position: 'relative', fontSize: 26, fontWeight: 900, lineHeight: 1.25 },
  label: { position: 'relative', fontSize: 12.5, fontWeight: 700, opacity: .95 },
  hint: { position: 'relative', fontSize: 10.5, opacity: .8, marginTop: 2 },

  card: {
    background: '#fff', borderRadius: 16, marginBottom: 16,
    border: '1px solid #e6e9f2', boxShadow: '0 4px 14px rgba(20,30,80,.07)',
    display: 'flex', flexDirection: 'column',
  },
  cardHead: {
    padding: '13px 16px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
  },
  cardTitle: { fontWeight: 900, color: '#1a237e', fontSize: 15 },
  headCell: {
    background: '#1a237e', color: '#fff', fontWeight: 700, fontSize: 11.5,
    whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 2,
  },
  cell: { fontSize: 12, padding: '9px 10px' },
  rowOn: { background: '#eef1ff', cursor: 'pointer', '&:hover': { background: '#e4e9ff' } },
  money: { fontWeight: 800, color: '#283593', whiteSpace: 'nowrap' },
  searchBox: { position: 'relative', display: 'flex', alignItems: 'center' },
  searchInput: {
    '& input': { paddingRight: 34 },
  },
  searchIcon: {
    position: 'absolute', right: 8, top: 9, color: '#9aa1c2', pointerEvents: 'none',
  },
  sumBox: {
    display: 'flex', gap: 14, flexWrap: 'wrap',
    padding: '10px 16px', background: '#f7f8fc', borderTop: '1px solid #eef0f8',
  },
  sumItem: { minWidth: 130 },
  sumLabel: { fontSize: 11, color: '#5a6b8c', fontWeight: 700 },
  sumValue: { fontSize: 15, color: '#1a237e', fontWeight: 900 },
  empty: { padding: 26, textAlign: 'center', color: '#8a92b2' },
}))

const STATS = [
  { key: 'contracts', label: 'قراردادها', hint: 'کلِ قراردادهای سازمان', bg: 'linear-gradient(135deg,#5b6ef5 0%,#8f7bff 100%)', icon: <AssessmentIcon /> },
  { key: 'contractors', label: 'پیمانکاران', hint: 'پیمانکارانِ یکتا', bg: 'linear-gradient(135deg,#f857a6 0%,#ff9a6b 100%)', icon: <PeopleIcon /> },
  { key: 'transfers_year', label: 'حواله‌های یک‌ساله', hint: '۳۶۵ روز اخیر', bg: 'linear-gradient(135deg,#06b875 0%,#4fd18b 100%)', icon: <SwapHorizIcon /> },
  { key: 'managers', label: 'مدیران سازمان', hint: 'حساب‌های مدیریت', bg: 'linear-gradient(135deg,#f0455b 0%,#ff8a5b 100%)', icon: <SupervisorAccountIcon /> },
]

/** داشبوردِ «مدیر سازمان» */
const DepartmentManagerDashboard = () => {
  const classes = useStyles()
  const navigate = useNavigate()
  const userInfo = useRecoilValue(userInfoState)
  const setUserInfo = useSetRecoilState(userInfoState)
  const [data, setData] = useState(null)
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [activeId, setActiveId] = useState(null)
  const [contracts, setContracts] = useState(null)
  const [cLoading, setCLoading] = useState(false)
  const [cq, setCq] = useState('')

  // ورود به پنلِ پیمانکار (مثلِ کاربرِ سازمان)
  const loginAsContractor = (contractorId) => {
    beginImpersonation(userInfo)
    api.post('users/login/' + contractorId).then((res) => {
      if (res.status === 200) {
        setUserInfo(res.data.user)
        localStorage.setItem('access', res.data.tokens.access)
        if (res.data.tokens.refresh) {
          localStorage.setItem('refresh', res.data.tokens.refresh)
        }
        api.defaults.headers.common['Authorization'] = 'Bearer ' + res.data.tokens.access
        window.location.href = '/'
      }
    }).catch(() => { /* خطا */ })
  }

  const load = useCallback(() => {
    setLoading(true)
    api.get('users/department-dashboard/', { params: { q } })
      .then((res) => {
        if (res.status === 200) { setData(res.data); setErr('') }
        else setErr('خطا در دریافت اطلاعات سازمان')
      })
      .catch(() => setErr('خطا در دریافت اطلاعات سازمان'))
      .finally(() => setLoading(false))
  }, [q])

  // جستجو با تأخیر (Debounce)
  useEffect(() => {
    const t = setTimeout(load, 350)
    return () => clearTimeout(t)
  }, [load])

  // انتخابِ خودکارِ اولین پیمانکار
  useEffect(() => {
    const list = data?.contractors || []
    if (!list.length) { setActiveId(null); setContracts(null); return }
    if (!list.some((c) => c.contractor_id === activeId)) {
      setActiveId(list[0].contractor_id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // بارگذاریِ قراردادهای پیمانکارِ فعال
  useEffect(() => {
    if (!activeId) { setContracts(null); return }
    setCLoading(true)
    api.get(`users/department-dashboard/contractors/${activeId}/`)
      .then((res) => { if (res.status === 200) setContracts(res.data) })
      .catch(() => setContracts(null))
      .finally(() => setCLoading(false))
  }, [activeId])

  const dept = data?.department
  const stats = data?.stats || {}
  const list = data?.contractors || []
  const active = useMemo(() => list.find((c) => c.contractor_id === activeId) || null, [list, activeId])
  const contractRows = useMemo(() => {
    const rows = contracts?.contracts || []
    const q = cq.trim()
    if (!q) return rows
    const en = toEnglishDigits(q)
    return rows.filter((c) =>
      String(c.number || '').includes(en) ||
      String(c.subject || '').includes(q)
    )
  }, [contracts, cq])

  return (
    <Layout>
      <div className={classes.root}>
        {/* ───── سربرگِ سازمان ───── */}
        <Box className={classes.hero} data-tour="dept-manager-hero">
          <span className={classes.heroBlob} />
          <span className={classes.heroBlob2} />
          {dept?.logo ? (
            <img src={logoSrc(dept.logo)} alt="" className={classes.logo} />
          ) : (
            <span className={classes.logoBox}><BusinessIcon style={{ fontSize: 38, color: '#fff' }} /></span>
          )}
          <Box style={{ flex: 1, minWidth: 220 }}>
            <Typography className={classes.heroTitle}>{dept?.name || 'پنل مدیر سازمان'}</Typography>
            {dept?.short_name && <Typography className={classes.heroSub}>{dept.short_name}</Typography>}
            <Box className={classes.heroChips}>
              <Chip className={classes.heroChip} size="small" label={`شناسه ملی: ${toFarsiDigits(dept?.national_code || '—')}`} />
              <Chip className={classes.heroChip} size="small"
                label={dept?.last_successful_upload
                  ? `آخرین بارگذاری موفق: ${gregorianToJalali(dept.last_successful_upload)}`
                  : 'هنوز بارگذاری موفقی ثبت نشده'} />
              {dept?.is_processing && (
                <Chip size="small" style={{ background: '#fff', color: '#283593', fontWeight: 700 }} label="در حال پردازش فایل" />
              )}
            </Box>
          </Box>

          {/* ───── مشخصاتِ خودِ مدیر سازمان ───── */}
          <Box className={classes.heroManager} data-tour="dept-manager-profile">
            {userInfo?.logo ? (
              <img src={logoSrc(userInfo.logo)} alt="" className={classes.managerAvatar}
                onError={(e) => { e.target.style.display = 'none' }} />
            ) : (
              <span className={classes.managerAvatarBox}>
                <PersonIcon style={{ fontSize: 28, color: '#fff' }} />
              </span>
            )}
            <Box>
              <Typography className={classes.heroManagerLabel}>مدیر سازمان</Typography>
              <Typography className={classes.heroManagerName}>
                {userInfo?.name || userInfo?.username || '—'}
              </Typography>
              {userInfo?.national_code && (
                <Typography className={classes.heroManagerCode}>
                  کد ملی: {toFarsiDigits(userInfo.national_code)}
                </Typography>
              )}
            </Box>
          </Box>
          <Button onClick={load} startIcon={<RefreshIcon />}
            style={{ position: 'relative', color: '#fff', border: '1px solid rgba(255,255,255,.45)' }}>
            به‌روزرسانی
          </Button>
        </Box>

        {err && <Typography color="error" style={{ marginBottom: 14 }}>{err}</Typography>}

        {/* ───── کارت‌های آماری ───── */}
        <Grid container spacing={2}>
          {STATS.map((it) => (
            <Grid item xs={6} md={3} key={it.key}>
              <Paper elevation={0} className={classes.statCard} style={{ background: it.bg }}>
                <span className={classes.blob} />
                <span className={classes.blob2} />
                <span className={classes.iconBox}>{it.icon}</span>
                <div>
                  <Typography className={classes.value}>{toFarsiDigits(stats[it.key] || 0)}</Typography>
                  <Typography className={classes.label}>{it.label}</Typography>
                  <Typography className={classes.hint}>{it.hint}</Typography>
                </div>
              </Paper>
            </Grid>
          ))}
        </Grid>

        <Grid container spacing={2}>
          {/* ───── سمت راست: پیمانکاران به‌ترتیبِ آخرین حواله + جستجو ───── */}
          <Grid item xs={12} md={4}>
            <Paper elevation={0} className={classes.card}>
              <div className={classes.cardHead}>
                <Typography className={classes.cardTitle}>پیمانکاران (آخرین حواله)</Typography>
                <Chip size="small" label={`${toFarsiDigits(list.length)} پیمانکار`}
                  style={{ background: '#eef1ff', color: '#283593', fontWeight: 700 }} />
              </div>
              <Divider />
              <Box className={classes.searchBox} style={{ padding: '10px 16px 0' }}>
                <SearchIcon className={classes.searchIcon} fontSize="small" />
                <TextField
                  size="small"
                  variant="outlined"
                  placeholder="جستجوی نام یا کد ملیِ پیمانکار…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className={classes.searchInput}
                  style={{ maxWidth: 320 }}
                  InputProps={{
                    startAdornment: <span style={{ width: 18 }} />,
                    endAdornment: q ? (
                      <IconButton size="small" onClick={() => setQ('')} title="پاک کردن">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : null,
                  }}
                />
              </Box>
                <TableContainer style={{ marginTop: 10, maxHeight: 470, overflowY: 'auto' }} data-tour="dept-manager-contractors">
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headCell} align="right"
                        style={{ textAlign: 'right' }}>پیمانکار</TableCell>
                      <TableCell className={classes.headCell} align="center">ورود</TableCell>
                        <TableCell className={classes.headCell} align="center">پیام</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {list.map((c) => (
                      <TableRow
                        key={c.contractor_id}
                        className={classes.rowOn}
                        onClick={() => setActiveId(c.contractor_id)}
                        style={activeId === c.contractor_id ? { background: '#e4e9ff' } : undefined}
                      >
                        <TableCell className={classes.cell} align="right" style={{ textAlign: 'right' }}>
                          <Typography style={{ fontWeight: 700, fontSize: 12.5, color: '#1a237e', textAlign: 'right' }}>
                            {toFarsiDigits(c.name)}
                          </Typography>
                        </TableCell>
                        <TableCell className={classes.cell} align="center">
                          <Button
                            size="small"
                            variant="contained"
                            color="primary"
                            onClick={(e) => { e.stopPropagation(); loginAsContractor(c.contractor_id) }}
                            style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: '2px 14px' }}
                          >
                            ورود
                          </Button>
                        </TableCell>
                        <TableCell className={classes.cell} align="center"
                          onClick={(e) => e.stopPropagation()}>
                          <RowActions userId={c.user_id} mobile={c.mobile} name={c.name} />
                        </TableCell>
                      </TableRow>
                    ))}
                    {!loading && !list.length && (
                      <TableRow>
                        <TableCell colSpan={1} align="center">
                          <Box className={classes.empty}>پیمانکاری یافت نشد.</Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* ───── سمت چپ: قراردادهای پیمانکارِ فعال ───── */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} className={classes.card}>
              <div className={classes.cardHead}>
                <Typography className={classes.cardTitle}>
                  قراردادهای {active ? toFarsiDigits(active.name) : '—'}
                </Typography>
                {contracts?.summary && (
                  <Chip size="small"
                    label={`${toFarsiDigits(contracts.summary.contracts)} قرارداد`}
                    style={{ background: '#e0f2f1', color: '#00796b', fontWeight: 700 }} />
                )}
              </div>
              <Divider />

              {/* ── نوارِ اطلاعاتِ پیمانکارِ انتخاب‌شده (پیام/پیامک در جدول سمت راست است) ── */}
              {active && (
                <Box style={{
                  display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
                  padding: '10px 16px', background: '#f7f9ff', borderBottom: '1px solid #e6e9f6',
                }}>
                  {[
                    ['شناسهٔ ملی', active.national_code],
                    ['شمارهٔ ثبت', active.registration_number],
                    ['کد اقتصادی', active.economic_code],
                    ['کد معاملاتی', active.brokerage_account],
                  ].map(([lbl, val]) => (
                    <span key={lbl} style={{ fontSize: 11.5, color: '#5b6180' }}>
                      {lbl}:{' '}
                      <b dir="ltr" style={{ color: '#1a237e' }}>
                        {val ? toFarsiDigits(val) : '—'}
                      </b>
                    </span>
                  ))}

                  <Chip size="small"
                    label={`${toFarsiDigits(active.contracts_count || 0)} قرارداد`}
                    style={{ background: '#e8eaf6', color: '#283593', fontWeight: 700 }} />
                  <Chip size="small"
                    label={`${toFarsiDigits(active.transfers_count || 0)} حواله`}
                    style={{ background: '#e0f2f1', color: '#00695c', fontWeight: 700 }} />
                  {active.last_transfer && (
                    <span style={{ fontSize: 11.5, color: '#5b6180' }}>
                      آخرین حواله: <b>{gregorianToJalali(active.last_transfer)}</b>
                    </span>
                  )}
                  <Chip size="small"
                    label={active.is_subscribed ? 'اشتراک فعال' : 'بدون اشتراک'}
                    style={{
                      fontWeight: 700,
                      background: active.is_subscribed ? '#e8f5e9' : '#ffebee',
                      color: active.is_subscribed ? '#2e7d32' : '#c62828',
                    }} />
                </Box>
              )}

              <Box className={classes.searchBox} style={{ padding: '8px 16px 0' }}>
                <SearchIcon className={classes.searchIcon} fontSize="small" />
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="جستجوی شماره یا موضوعِ قرارداد…"
                  value={cq}
                  onChange={(e) => setCq(e.target.value)}
                  className={classes.searchInput}
                  InputProps={{
                    startAdornment: <span style={{ width: 20 }} />,
                    endAdornment: cq ? (
                      <IconButton size="small" onClick={() => setCq('')} title="پاک کردن">
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : null,
                  }}
                />
              </Box>
              {cLoading && (
                <Box style={{ padding: 26, textAlign: 'center' }}><CircularProgress size={26} /></Box>
              )}
              {!cLoading && contracts && (
                <TableContainer style={{ maxHeight: 400, overflowY: 'auto' }} data-tour="dept-manager-contracts">
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headCell} align="center">شماره</TableCell>
                        <TableCell className={classes.headCell} align="right"
                          style={{ textAlign: 'right' }}>تاریخ</TableCell>
                        <TableCell className={classes.headCell} align="center">مبلغ اولیه</TableCell>
                        <TableCell className={classes.headCell} align="center">افزایش/کاهش</TableCell>
                        <TableCell className={classes.headCell} align="center">جمع مبلغ</TableCell>
                        <TableCell className={classes.headCell} align="center">جمع صورت وضعیت عادی</TableCell>
                        <TableCell className={classes.headCell} align="center">پرداخت شده</TableCell>
                        <TableCell className={classes.headCell} align="center">مانده</TableCell>
                        <TableCell className={classes.headCell} align="center">پیش‌پرداخت</TableCell>
                        <TableCell className={classes.headCell} align="center">تسویه</TableCell>
                        <TableCell className={classes.headCell} align="center">مانده پیش‌پرداخت</TableCell>
                        <TableCell className={classes.headCell} align="center">گزارش</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contractRows.map((c) => (
                        <TableRow key={c.id}>
                          <TableCell className={classes.cell} align="center" dir="ltr">
                            {toFarsiDigits(c.number)}
                          </TableCell>
                          <TableCell className={classes.cell} align="right" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {c.date ? gregorianToJalali(c.date) : '—'}
                          </TableCell>
                          <TableCell className={classes.cell} align="center">
                            <span className={classes.money}>
                              {toFarsiDigits(thousandsSeparate(c.contract_cost || 0))}
                            </span>
                          </TableCell>
                          <TableCell className={classes.cell} align="center">
                            <span style={{ fontWeight: 700, color: (c.increase_decrease_cost || 0) < 0 ? '#c62828' : '#2e7d32' }}>
                              {toFarsiDigits(thousandsSeparate(c.increase_decrease_cost || 0))}
                            </span>
                          </TableCell>
                          <TableCell className={classes.cell} align="center">
                            <span className={classes.money}>
                              {toFarsiDigits(thousandsSeparate(c.contract_total || 0))}
                            </span>
                          </TableCell>
                          <TableCell className={classes.cell} align="center">
                            <span className={classes.money}>
                              {toFarsiDigits(thousandsSeparate(c.request_sum || 0))}
                            </span>
                          </TableCell>
                          <TableCell className={classes.cell} align="center">
                            <span className={classes.money} style={{ color: '#00796b' }}>
                              {toFarsiDigits(thousandsSeparate(c.paid || 0))}
                            </span>
                          </TableCell>
                          <TableCell className={classes.cell} align="center">
                            <span className={classes.money} style={{ color: '#c62828' }}>
                              {toFarsiDigits(thousandsSeparate(c.remaining_payment || 0))}
                            </span>
                          </TableCell>
                          <TableCell className={classes.cell} align="center">
                            <span className={classes.money} style={{ color: '#1565c0' }}>
                              {toFarsiDigits(thousandsSeparate(c.prepayment_total || 0))}
                            </span>
                          </TableCell>
                          <TableCell className={classes.cell} align="center">
                            <span className={classes.money} style={{ color: '#2e7d32' }}>
                              {toFarsiDigits(thousandsSeparate(c.settlement_total || 0))}
                            </span>
                          </TableCell>
                          <TableCell className={classes.cell} align="center">
                            <span className={classes.money}
                              style={{ color: (c.prepayment_balance || 0) < 0 ? '#c62828' : '#6a1b9a' }}>
                              {toFarsiDigits(thousandsSeparate(c.prepayment_balance || 0))}
                            </span>
                          </TableCell>
                          <TableCell className={classes.cell} align="center">
                            {c.has_prepayment ? (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => navigate(`/contracts/${c.id}/prepayment?ctx=contractor`)}
                                style={{
                                  background: '#6a1b9a', color: '#fff', fontWeight: 700,
                                  textTransform: 'none', fontSize: 11.5, whiteSpace: 'nowrap',
                                }}
                              >
                                پیش‌پرداخت
                              </Button>
                            ) : (
                              <span style={{ color: '#c3c8d8' }}>—</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                      {!contractRows.length && (
                        <TableRow>
                          <TableCell colSpan={8} align="center">
                            <Box className={classes.empty}>
                              {cq.trim() ? 'قراردادی با این جستجو یافت نشد.' : 'قراردادی برای این پیمانکار یافت نشد.'}
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </div>
    </Layout>
  )
}

export default DepartmentManagerDashboard
